import React, { useState, useMemo, useCallback } from 'react';
import {
    Table, Button, Modal, Form, Input, Select, InputNumber, Space,
    Typography, Tag, message, Popconfirm, Upload, Radio, Alert, Tooltip,
    Row, Col, Card, Divider, Dropdown, MenuProps, theme,
} from 'antd';
import {
    PlusOutlined, DeleteOutlined, EditOutlined, DownloadOutlined,
    UploadOutlined, FileExcelOutlined, UndoOutlined, ExpandAltOutlined,
    EllipsisOutlined, TableOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { financeApi } from '../../../api/client';

const { Text } = Typography;
const { Option } = Select;

const MONTHS = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];

const COST_CATEGORIES = [
    { value: 'PROCUREMENT', label: 'Zaopatrzenie' },
    { value: 'COGS', label: 'Koszt sprzedanych towarów' },
    { value: 'MARKETING', label: 'Marketing' },
    { value: 'OPERATIONS', label: 'Operacje' },
    { value: 'PERSONNEL', label: 'Kadry' },
    { value: 'TECHNOLOGY', label: 'Technologia' },
    { value: 'LOGISTICS', label: 'Logistyka' },
    { value: 'LEGAL', label: 'Prawne' },
    { value: 'OTHER', label: 'Inne' },
];

interface CostsPlanTabProps {
    year?: number;
}

const CostsPlanTab: React.FC<CostsPlanTabProps> = ({ year: propYear }) => {
    const { token } = theme.useToken();
    const [selectedYear, setSelectedYear] = useState(propYear || new Date().getFullYear());
    const [viewMode, setViewMode] = useState<'plan' | 'actual' | 'delta' | 'both'>('plan');
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [elementModalVisible, setElementModalVisible] = useState(false);
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [addForm] = Form.useForm();
    const [elementForm] = Form.useForm();
    const [editingElement, setEditingElement] = useState<any>(null);
    const [editingParent, setEditingParent] = useState<any>(null);
    const [pendingUpdates, setPendingUpdates] = useState<Map<string, any>>(new Map());
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importStrategy, setImportStrategy] = useState<string>('merge_update');
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const queryClient = useQueryClient();

    // Fetch budget grid
    const { data: gridRows = [], isLoading } = useQuery({
        queryKey: ['budget-grid', selectedYear],
        queryFn: async () => (await financeApi.getBudgetGrid(selectedYear)).data,
    });

    // Fetch initiatives
    const { data: initiatives = [] } = useQuery({
        queryKey: ['strategic-initiatives-plan'],
        queryFn: async () => (await financeApi.getStrategicInitiatives()).data,
    });

    // Fetch actuals overview
    const { data: overview } = useQuery({
        queryKey: ['costs-overview-plan', selectedYear],
        queryFn: async () => (await financeApi.getCostsOverview({ year: selectedYear })).data,
    });

    const actualsByMonth = useMemo(() => {
        const map: Record<string, Record<number, number>> = {};
        if (overview?.budgetVsActual) {
            for (const entry of overview.budgetVsActual) {
                const key = 'ALL'; // aggregated, not per-category from overview
                if (!map[key]) map[key] = {};
                map[key][entry.month] = (map[key][entry.month] || 0) + entry.actual;
            }
        }
        return map;
    }, [overview]);

    // Mutations
    const saveMutation = useMutation({
        mutationFn: (updates: any[]) => financeApi.batchUpdateBudgetCells(updates),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['budget-grid'] });
            setPendingUpdates(new Map());
            message.success('Budżet zapisany');
        },
    });

    const addLineMutation = useMutation({
        mutationFn: (data: any) => financeApi.createBudgetLine(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['budget-grid'] });
            setAddModalVisible(false);
            addForm.resetFields();
            message.success('Pozycja dodana');
        },
    });

    const softDeleteMutation = useMutation({
        mutationFn: (data: { name: string; category: string; year: number }) => financeApi.softDeleteBudgetPosition(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['budget-grid'] });
            message.success('Pozycja usunięta (soft)');
        },
    });

    const restoreMutation = useMutation({
        mutationFn: (data: { name: string; category: string; year: number }) => financeApi.restoreBudgetPosition(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['budget-grid'] });
            message.success('Pozycja przywrócona');
        },
    });

    const saveElementMutation = useMutation({
        mutationFn: (data: any) => financeApi.saveBudgetElement(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['budget-grid'] });
            setElementModalVisible(false);
            elementForm.resetFields();
            setEditingElement(null);
            message.success('Element zapisany');
        },
    });

    const deleteElementMutation = useMutation({
        mutationFn: (id: string) => financeApi.deleteBudgetElement(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['budget-grid'] });
            message.success('Element usunięty');
        },
    });

    const importMutation = useMutation({
        mutationFn: () => financeApi.importBudget(importFile!, selectedYear, importStrategy),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['budget-grid'] });
            setImportModalVisible(false);
            setImportFile(null);
            message.success(`Import zakończony: ${res.data.imported} dodanych, ${res.data.updated} zaktualizowanych, ${res.data.skipped} pominiętych`);
        },
    });

    // Cell edit
    const handleCellChange = (row: any, month: number, value: number) => {
        // Only elements are editable according to requirement
        if (!row.isElement) return;

        const key = `element|${row.id}|${month}`;
        const update = {
            id: row.id,
            month,
            value: value || 0,
            originalRow: row
        };
        setPendingUpdates(prev => new Map(prev).set(key, update));
    };

    const handleSave = async () => {
        const updates = Array.from(pendingUpdates.values());
        if (updates.length === 0) return;

        // Group updates by element ID to perform individual saveBudgetElement calls
        // since there is no batch update for elements yet.
        const elementUpdates = new Map<string, any>();
        updates.forEach(u => {
            if (!elementUpdates.has(u.id)) {
                const currentMonths = u.originalRow.months || {};
                elementUpdates.set(u.id, {
                    id: u.id,
                    budgetPositionName: u.originalRow._parent.name,
                    budgetPositionCategory: u.originalRow._parent.category,
                    name: u.originalRow.name,
                    year: selectedYear,
                    months: { ...currentMonths }
                });
            }
            elementUpdates.get(u.id).months[u.month] = u.value;
        });

        try {
            for (const data of elementUpdates.values()) {
                await financeApi.saveBudgetElement(data);
            }
            queryClient.invalidateQueries({ queryKey: ['budget-grid'] });
            setPendingUpdates(new Map());
            message.success('Zmiany zostały zapisane');
        } catch (e) {
            message.error('Wystąpił błąd podczas zapisywania zmian');
        }
    };

    // Export helpers
    const handleExportBudget = () => {
        const authStore = localStorage.getItem('curecraft-auth');
        let token = '';
        if (authStore) try { token = JSON.parse(authStore).state?.token || ''; } catch { }
        window.open(`${financeApi.exportBudgetUrl}?year=${selectedYear}&token=${token}`, '_blank');
    };

    const handleDownloadTemplate = () => {
        const authStore = localStorage.getItem('curecraft-auth');
        let token = '';
        if (authStore) try { token = JSON.parse(authStore).state?.token || ''; } catch { }
        window.open(`${financeApi.budgetTemplateUrl}?token=${token}`, '_blank');
    };

    // Element modal
    const openElementModal = (parent: any, element?: any) => {
        setEditingParent(parent);
        setEditingElement(element || null);
        elementForm.resetFields();
        if (element) {
            elementForm.setFieldsValue({ name: element.name, ...Object.fromEntries(Object.entries(element.months || {}).map(([k, v]) => [`month_${k}`, v])) });
        }
        setElementModalVisible(true);
    };

    const handleElementSubmit = async () => {
        const values = await elementForm.validateFields();
        const months: Record<number, number> = {};
        for (let m = 1; m <= 12; m++) {
            months[m] = Number(values[`month_${m}`] || 0);
        }
        saveElementMutation.mutate({
            id: editingElement?.id,
            budgetPositionName: editingParent.name,
            budgetPositionCategory: editingParent.category,
            name: values.name,
            year: selectedYear,
            months,
        });
    };

    // Toggle row expand
    const toggleExpand = (rowId: string) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
            return next;
        });
    };

    // Flatten data: interleave elements after their parent row
    const tableData = useMemo(() => {
        const result: any[] = [];
        for (const row of gridRows) {
            // Track element totals manually for better recalculation display
            const elementSums: Record<number, number> = {};
            if (row.elements) {
                row.elements.forEach((el: any) => {
                    Object.entries(el.months || {}).forEach(([m, v]) => {
                        elementSums[Number(m)] = (elementSums[Number(m)] || 0) + Number(v);
                    });
                });
            }

            result.push({ ...row, elementSums });
            if (expandedRows.has(row.id) && row.elements?.length > 0) {
                for (const el of row.elements) {
                    result.push({ ...el, isElement: true, _parent: row, id: el.id });
                }
            }
        }
        return result;
    }, [gridRows, expandedRows]);

    // Build table columns
    const columns: any[] = [
        {
            title: 'Pozycja', dataIndex: 'name', key: 'name', width: 250, fixed: 'left',
            render: (name: string, record: any) => (
                <Space>
                    {record.elements?.length > 0 && (
                        <Button
                            size="small"
                            type="text"
                            icon={<ExpandAltOutlined style={{ transform: expandedRows.has(record.id) ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s' }} />}
                            onClick={() => toggleExpand(record.id)}
                        />
                    )}
                    <div style={record.isElement ? { paddingLeft: 24 } : {}}>
                        <Text strong={!record.isElement} style={{ fontSize: record.isElement ? 13 : 14 }}>{name}</Text>
                        {record.isDeleted && <Tag color="red" style={{ marginLeft: 8 }}>usunięte</Tag>}
                    </div>
                </Space>
            ),
        },
        {
            title: 'Kategoria', dataIndex: 'category', key: 'category', width: 150,
            render: (c: string, record: any) => {
                if (record.isElement) return null;
                const cat = COST_CATEGORIES.find(cc => cc.value === c);
                return <Tag style={{ margin: 0 }}>{cat?.label || c}</Tag>;
            },
        },
    ];

    for (let m = 1; m <= 12; m++) {
        const month = m;
        columns.push({
            title: MONTHS[m - 1], key: `m${m}`, width: 110, align: 'right',
            render: (_: any, record: any) => {
                if (record.isElement) {
                    const pendingKey = `element|${record.id}|${month}`;
                    const pendingVal = pendingUpdates.get(pendingKey);
                    const val = pendingVal ? pendingVal.value : (record.months?.[month] || 0);

                    if (viewMode === 'plan') {
                        return (
                            <InputNumber
                                size="small"
                                value={val}
                                onChange={(v) => handleCellChange(record, month, v || 0)}
                                style={{ width: '100%' }}
                                bordered={false}
                                controls={false}
                                precision={0}
                                decimalSeparator=","
                                onFocus={(e) => e.target.select()}
                                addonAfter={<span style={{ fontSize: 10 }}>zł</span>}
                                inputMode="decimal"
                                disabled={record._parent?.isDeleted}
                            />
                        );
                    }
                    return <Text type="secondary">{val > 0 ? val.toLocaleString() : '—'}</Text>;
                }

                // Parent row - sum of elements
                const plannedSum = record.elementSums?.[month] || 0;
                // Add pending updates for display
                let currentTotal = plannedSum;
                pendingUpdates.forEach((u) => {
                    if (u.originalRow._parent.id === record.id && u.month === month) {
                        const originalVal = u.originalRow.months?.[month] || 0;
                        currentTotal = currentTotal - originalVal + u.value;
                    }
                });

                const actual = actualsByMonth['ALL']?.[month] || 0;

                if (viewMode === 'plan') {
                    // Conditional formatting: if actual > planned → red, if actual < planned → green
                    const actualVal = actualsByMonth['ALL']?.[month] || 0;
                    let bgColor: string | undefined;
                    if (currentTotal > 0 && actualVal > 0) {
                        if (actualVal > currentTotal) bgColor = token.colorErrorBg;
                        else if (actualVal < currentTotal) bgColor = token.colorSuccessBg;
                    }
                    return <div style={{ background: bgColor, padding: '2px 4px', borderRadius: 4 }}><Text strong style={{ fontSize: 13 }}>{currentTotal > 0 ? currentTotal.toLocaleString() : '—'}</Text></div>;
                } else if (viewMode === 'actual') {
                    return <Text>{actual > 0 ? actual.toLocaleString() : '—'}</Text>;
                } else if (viewMode === 'delta') {
                    const delta = actual - currentTotal;
                    return <Text type={delta > 0 ? 'danger' : delta < 0 ? 'success' : undefined}>{delta !== 0 ? delta.toLocaleString() : '—'}</Text>;
                } else {
                    // Combined view with conditional formatting
                    const delta = actual - currentTotal;
                    let bgColor: string | undefined;
                    if (currentTotal > 0 && actual > 0) {
                        if (actual > currentTotal) bgColor = token.colorErrorBg;
                        else if (actual < currentTotal) bgColor = token.colorSuccessBg;
                    }
                    return (
                        <div style={{ fontSize: 11, lineHeight: '16px', background: bgColor, padding: '2px 4px', borderRadius: 4 }}>
                            <div>P: {currentTotal > 0 ? currentTotal.toLocaleString() : '—'}</div>
                            <div>A: {actual > 0 ? actual.toLocaleString() : '—'}</div>
                            <div style={{ color: delta > 0 ? token.colorError : token.colorSuccess }}>
                                Δ: {delta !== 0 ? delta.toLocaleString() : '—'}
                            </div>
                        </div>
                    );
                }
            },
        });
    }

    columns.push({
        title: '', key: 'actions', width: 60, fixed: 'right', align: 'center',
        render: (_: any, record: any) => {
            const items: MenuProps['items'] = record.isElement ? [
                { key: 'edit', label: 'Edytuj', icon: <EditOutlined />, onClick: () => openElementModal(record._parent, record) },
                {
                    key: 'delete', label: 'Usuń', icon: <DeleteOutlined />, danger: true,
                    onClick: () => {
                        Modal.confirm({
                            title: 'Usunąć element?',
                            content: `Czy na pewno chcesz usunąć element "${record.name}"?`,
                            okText: 'Usuń',
                            okType: 'danger',
                            cancelText: 'Anuluj',
                            onOk: () => deleteElementMutation.mutate(record.id)
                        });
                    }
                },
            ] : [
                { key: 'add', label: 'Dodaj element', icon: <PlusOutlined />, onClick: () => openElementModal(record) },
                record.isDeleted ?
                    { key: 'restore', label: 'Przywróć', icon: <UndoOutlined />, onClick: () => restoreMutation.mutate({ name: record.name, category: record.category, year: selectedYear }) } :
                    {
                        key: 'delete', label: 'Usuń', icon: <DeleteOutlined />, danger: true,
                        onClick: () => {
                            Modal.confirm({
                                title: 'Usunąć pozycję budżetową?',
                                content: `Czy na pewno chcesz usunąć "${record.name}"? Wszystkie podelementy zostaną również usunięte.`,
                                okText: 'Usuń',
                                okType: 'danger',
                                cancelText: 'Anuluj',
                                onOk: () => softDeleteMutation.mutate({ name: record.name, category: record.category, year: selectedYear })
                            });
                        }
                    },
            ];

            return (
                <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
                    <Button type="text" size="small" icon={<EllipsisOutlined />} />
                </Dropdown>
            );
        },
    });

    return (
        <Card size="small" bordered={false} bodyStyle={{ padding: 0 }}>
            {/* Toolbar */}
            <div style={{
                padding: '12px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: token.colorBgContainer,
                borderBottom: `1px solid ${token.colorBorderSecondary}`
            }}>
                <Space split={<Divider type="vertical" />}>
                    <Space>
                        <Select value={selectedYear} onChange={setSelectedYear} style={{ width: 100 }} size="small">
                            {[2024, 2025, 2026, 2027].map(y => <Option key={y} value={y}>{y}</Option>)}
                        </Select>
                        <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} buttonStyle="solid" size="small">
                            <Radio.Button value="plan">Plan</Radio.Button>
                            <Radio.Button value="actual">Actual</Radio.Button>
                            <Radio.Button value="delta">Δ Delta</Radio.Button>
                            <Radio.Button value="both">Oba</Radio.Button>
                        </Radio.Group>
                    </Space>

                    <Space>
                        <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate} size="small">Szablon</Button>
                        <Button icon={<UploadOutlined />} onClick={() => setImportModalVisible(true)} size="small">Import</Button>
                        <Button icon={<FileExcelOutlined />} onClick={handleExportBudget} size="small">Eksport</Button>
                        <Button icon={<PlusOutlined />} onClick={() => setAddModalVisible(true)} size="small" type="primary">Dodaj pozycję</Button>
                    </Space>
                </Space>

                <Space>
                    {pendingUpdates.size > 0 && (
                        <Button type="primary" onClick={handleSave} loading={saveMutation.isPending} size="small">
                            Zapisz zmiany ({pendingUpdates.size})
                        </Button>
                    )}
                    <Tooltip title="Przeładuj dane">
                        <Button icon={<UndoOutlined />} size="small" onClick={() => queryClient.invalidateQueries({ queryKey: ['budget-grid'] })} />
                    </Tooltip>
                </Space>
            </div>

            <Table
                dataSource={tableData}
                columns={columns}
                rowKey={(record) => record.isElement ? `el-${record.id}` : record.id}
                loading={isLoading}
                size="small"
                pagination={false}
                bordered
                scroll={{ x: 'max-content', y: 'calc(100vh - 350px)' }}
                rowClassName={(record: any) => record.isElement ? 'element-row' : record.isDeleted ? 'deleted-row' : 'parent-row'}
                summary={(pageData) => {
                    if (pageData.length === 0) return null;

                    const totals: Record<number, number> = {};
                    pageData.forEach((row) => {
                        if (!row.isElement) {
                            Object.entries(row.elementSums || {}).forEach(([m, v]) => {
                                totals[Number(m)] = (totals[Number(m)] || 0) + (v as number);
                            });
                        }
                    });

                    // Adjust totals for pending updates
                    pendingUpdates.forEach((u) => {
                        const originalVal = u.originalRow.months?.[u.month] || 0;
                        totals[u.month] = (totals[u.month] || 0) - originalVal + u.value;
                    });

                    return (
                        <Table.Summary fixed="bottom">
                            <Table.Summary.Row style={{ background: token.colorFillAlter }}>
                                <Table.Summary.Cell index={0} colSpan={2}>
                                    <Text strong>TOTAL</Text>
                                </Table.Summary.Cell>
                                {Array.from({ length: 12 }).map((_, i) => (
                                    <Table.Summary.Cell index={i + 2} key={i} align="right">
                                        <Text strong>{(totals[i + 1] || 0).toLocaleString()}</Text>
                                    </Table.Summary.Cell>
                                ))}
                                <Table.Summary.Cell index={14} />
                            </Table.Summary.Row>
                        </Table.Summary>
                    );
                }}
            />

            {/* Add Position Modal */}
            <Modal
                title="Dodaj pozycję budżetową"
                open={addModalVisible}
                onCancel={() => { setAddModalVisible(false); addForm.resetFields(); }}
                onOk={async () => {
                    const values = await addForm.validateFields();
                    addLineMutation.mutate({ ...values, year: selectedYear, month: 1, plannedAmount: 0 });
                }}
                okText="Dodaj"
            >
                <Form form={addForm} layout="vertical">
                    <Form.Item name="name" label="Nazwa pozycji" rules={[{ required: true }]}>
                        <Input placeholder="np. Reklamy, Narzędzia SaaS" />
                    </Form.Item>
                    <Form.Item name="category" label="Kategoria" rules={[{ required: true }]}>
                        <Select>
                            {COST_CATEGORIES.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                        </Select>
                    </Form.Item>
                    <Form.Item name="initiativeId" label="Inicjatywa strategiczna">
                        <Select allowClear placeholder="Opcjonalnie">
                            {initiatives.map((i: any) => <Option key={i.id} value={i.id}>{i.name}</Option>)}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Element Modal */}
            <Modal
                title={editingElement ? 'Edytuj element' : `Dodaj element do "${editingParent?.name}"`}
                open={elementModalVisible}
                onCancel={() => { setElementModalVisible(false); elementForm.resetFields(); setEditingElement(null); }}
                onOk={handleElementSubmit}
                okText={editingElement ? 'Zapisz' : 'Dodaj'}
                width={700}
            >
                <Form form={elementForm} layout="vertical">
                    <Form.Item name="name" label="Nazwa elementu" rules={[{ required: true }]}>
                        <Input placeholder="np. Google Ads, Facebook Ads" />
                    </Form.Item>
                    <Divider orientation="left">Kwoty miesięczne (PLN)</Divider>
                    <Row gutter={8}>
                        {MONTHS.map((m, i) => (
                            <Col span={4} key={i}>
                                <Form.Item name={`month_${i + 1}`} label={m}>
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        precision={0}
                                        min={0}
                                        size="small"
                                        decimalSeparator=","
                                        onFocus={(e) => e.target.select()}
                                        addonAfter="zł"
                                    />
                                </Form.Item>
                            </Col>
                        ))}
                    </Row>
                </Form>
            </Modal>

            {/* Import Modal */}
            <Modal
                title="Import budżetu z pliku Excel"
                open={importModalVisible}
                onCancel={() => { setImportModalVisible(false); setImportFile(null); }}
                onOk={() => importMutation.mutate()}
                okText="Importuj"
                confirmLoading={importMutation.isPending}
                okButtonProps={{ disabled: !importFile }}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Alert
                        message="Wybierz strategię importu"
                        description="Określ jak postąpić z istniejącymi danymi budżetowymi."
                        type="info"
                        showIcon
                    />
                    <Radio.Group value={importStrategy} onChange={e => setImportStrategy(e.target.value)}>
                        <Space direction="vertical">
                            <Radio value="replace_all">
                                <Text strong>Zastąp wszystko</Text>
                                <br />
                                <Text type="secondary">Usuń istniejące pozycje i zastąp importowanymi</Text>
                            </Radio>
                            <Radio value="add_missing">
                                <Text strong>Dodaj tylko brakujące</Text>
                                <br />
                                <Text type="secondary">Dodaj pozycje, które jeszcze nie istnieją, nie modyfikuj istniejących</Text>
                            </Radio>
                            <Radio value="merge_update">
                                <Text strong>Scalaj i aktualizuj</Text>
                                <br />
                                <Text type="secondary">Zaktualizuj istniejące pozycje i dodaj nowe</Text>
                            </Radio>
                        </Space>
                    </Radio.Group>
                    <Upload
                        accept=".xlsx,.xls"
                        maxCount={1}
                        beforeUpload={(file) => { setImportFile(file); return false; }}
                        onRemove={() => setImportFile(null)}
                    >
                        <Button icon={<UploadOutlined />}>Wybierz plik Excel</Button>
                    </Upload>
                </Space>
            </Modal>

            <style>{`
                .element-row { 
                    background: ${token.colorFillQuaternary} !important; 
                }
                .element-row:hover {
                    background: ${token.colorFillTertiary} !important;
                }
                .deleted-row { 
                    opacity: 0.5; 
                    background: ${token.colorErrorBg} !important; 
                }
                .parent-row {
                    background: ${token.colorBgContainer};
                }
                .ant-table-summary .ant-table-cell {
                    background: ${token.colorFillAlter} !important;
                    border-top: 2px solid ${token.colorBorder} !important;
                }
            `}</style>
        </Card>
    );
};

export default CostsPlanTab;
